import React, { useEffect, useMemo, useState } from "react";
import { motion } from "framer-motion";
import { Phone, ChevronLeft, ChevronRight, ArrowLeft, Clock, CalendarCheck, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { api } from "../../lib/api";

const MONTHS = ["Janvier", "Février", "Mars", "Avril", "Mai", "Juin", "Juillet", "Août", "Septembre", "Octobre", "Novembre", "Décembre"];
const WEEKDAYS = ["Lun", "Mar", "Mer", "Jeu", "Ven", "Sam", "Dim"];

const dayKeyParis = (iso) => new Intl.DateTimeFormat("fr-CA", { timeZone: "Europe/Paris", year: "numeric", month: "2-digit", day: "2-digit" }).format(new Date(iso));
const timeParis = (iso) => new Intl.DateTimeFormat("fr-FR", { timeZone: "Europe/Paris", hour: "2-digit", minute: "2-digit" }).format(new Date(iso));
const longDayParis = (iso) => new Intl.DateTimeFormat("fr-FR", { timeZone: "Europe/Paris", weekday: "long", day: "numeric", month: "long" }).format(new Date(iso));
const cellKey = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;

export default function Planning() {
  const [dash, setDash] = useState(null);
  const [loading, setLoading] = useState(true);
  const [slots, setSlots] = useState([]);
  const [questions, setQuestions] = useState([]);
  const [cursor, setCursor] = useState(new Date());
  const [selectedDay, setSelectedDay] = useState(null); // dayKey
  const [selectedSlot, setSelectedSlot] = useState(null); // iso
  const [answers, setAnswers] = useState([]);
  const [booking, setBooking] = useState(false);
  const [confirmed, setConfirmed] = useState(null);

  const loadDash = () => api.get("/client/dashboard").then(({ data }) => setDash(data)).catch(() => {});

  useEffect(() => {
    loadDash();
    api.get("/client/calendly/availability")
      .then(({ data }) => {
        setSlots(data.slots || []);
        setQuestions(data.questions || []);
        setAnswers((data.questions || []).map(() => ""));
      })
      .catch(() => toast.error("Impossible de charger les disponibilités."))
      .finally(() => setLoading(false));
  }, []);

  // group slots by Paris day
  const slotsByDay = useMemo(() => {
    const map = {};
    slots.forEach((iso) => {
      const k = dayKeyParis(iso);
      (map[k] = map[k] || []).push(iso);
    });
    Object.values(map).forEach((arr) => arr.sort());
    return map;
  }, [slots]);

  const year = cursor.getFullYear();
  const month = cursor.getMonth();
  const cells = useMemo(() => {
    const first = new Date(year, month, 1);
    const offset = (first.getDay() + 6) % 7;
    const days = new Date(year, month + 1, 0).getDate();
    const arr = [];
    for (let i = 0; i < offset; i++) arr.push(null);
    for (let d = 1; d <= days; d++) arr.push(new Date(year, month, d));
    while (arr.length % 7 !== 0) arr.push(null);
    return arr;
  }, [year, month]);

  const next_call = dash?.deadlines?.next_call;

  const pickDay = (d) => {
    const k = cellKey(d);
    if (!slotsByDay[k]) return;
    setSelectedDay(k);
    setSelectedSlot(null);
  };

  const submitBooking = async () => {
    for (let i = 0; i < questions.length; i++) {
      if (questions[i].required && !answers[i].trim()) {
        toast.error("Merci de répondre à toutes les questions.");
        return;
      }
    }
    setBooking(true);
    try {
      const { data } = await api.post("/client/calendly/book", { start_time: selectedSlot, answers });
      setConfirmed({ when: data.when });
      setSlots((prev) => prev.filter((s) => s !== selectedSlot));
      setSelectedDay(null);
      setSelectedSlot(null);
      setAnswers(questions.map(() => ""));
      loadDash();
      toast.success("Ton rendez-vous est confirmé ✨");
    } catch (e) {
      toast.error(e.response?.data?.detail || "La réservation a échoué.");
    } finally {
      setBooking(false);
    }
  };

  const daySlots = selectedDay ? (slotsByDay[selectedDay] || []) : [];

  return (
    <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} className="max-w-3xl space-y-8">
      <div>
        <p className="eyebrow">Ton agenda</p>
        <h1 className="mt-2 font-sans text-3xl font-medium text-ink sm:text-4xl">Planning de mes RDVs</h1>
        <p className="mt-2 font-light text-ink-soft">Choisis un créneau pour ton appel de consulting 1:1 avec Fanny.</p>
      </div>

      <div className="grid gap-3">
        <div data-testid="planning-next-call" className="flex items-center gap-3 rounded-xl border border-line bg-white p-4 shadow-card">
          <span className="flex h-10 w-10 items-center justify-center rounded-xl bg-bordeaux-tint text-bordeaux"><Phone size={18} /></span>
          <div>
            <p className="text-xs text-ink-muted">Prochain appel</p>
            <p className="text-sm font-medium text-ink">{next_call || "À planifier"}</p>
          </div>
        </div>
      </div>

      {confirmed && (
        <div data-testid="booking-confirmed" className="flex items-center gap-3 rounded-2xl border border-bordeaux-border bg-bordeaux text-white p-5 shadow-card">
          <CalendarCheck size={22} />
          <p className="text-sm font-medium">Rendez-vous confirmé le {confirmed.when}. Tu le retrouves sur ton tableau de bord.</p>
        </div>
      )}

      {/* Booking flow */}
      <div data-testid="custom-calendar" className="rounded-2xl border border-line bg-white p-5 shadow-card sm:p-6">
        <p className="eyebrow mb-4">Réserver un appel de consulting 1:1</p>

        {loading ? (
          <div className="flex items-center gap-2 py-10 text-ink-muted"><Loader2 size={18} className="animate-spin" /> Chargement des disponibilités…</div>
        ) : slots.length === 0 ? (
          <p className="py-8 text-center text-sm text-ink-muted">Aucun créneau disponible pour le moment. Reviens bientôt 🌸</p>
        ) : !selectedDay ? (
          <>
            <div className="mb-4 flex items-center justify-between">
              <button data-testid="cal-prev" onClick={() => setCursor(new Date(year, month - 1, 1))} className="rounded-full border border-line p-2 text-ink-soft transition hover:border-bordeaux hover:text-bordeaux"><ChevronLeft size={18} /></button>
              <span className="font-sans text-lg font-medium text-ink">{MONTHS[month]} {year}</span>
              <button data-testid="cal-next" onClick={() => setCursor(new Date(year, month + 1, 1))} className="rounded-full border border-line p-2 text-ink-soft transition hover:border-bordeaux hover:text-bordeaux"><ChevronRight size={18} /></button>
            </div>
            <div className="grid grid-cols-7 gap-1">
              {WEEKDAYS.map((d) => <div key={d} className="py-2 text-center text-xs font-medium uppercase tracking-wider text-ink-muted">{d}</div>)}
              {cells.map((d, i) => {
                if (!d) return <div key={i} />;
                const k = cellKey(d);
                const has = !!slotsByDay[k];
                return (
                  <button
                    key={i}
                    disabled={!has}
                    onClick={() => pickDay(d)}
                    data-testid={has ? `cal-available-${d.getDate()}` : `cal-day-${d.getDate()}`}
                    className={`flex h-12 flex-col items-center justify-center rounded-xl text-sm transition ${
                      has ? "bg-bordeaux-tint font-medium text-bordeaux hover:bg-bordeaux hover:text-white cursor-pointer ring-1 ring-bordeaux-border" : "text-ink-muted/50 cursor-default"
                    }`}
                  >
                    {d.getDate()}
                    {has && <span className="mt-0.5 h-1 w-1 rounded-full bg-current" />}
                  </button>
                );
              })}
            </div>
            <p className="mt-4 text-xs text-ink-muted">Les jours en bordeaux ont des créneaux disponibles (heure de Paris).</p>
          </>
        ) : !selectedSlot ? (
          <>
            <button onClick={() => setSelectedDay(null)} className="mb-4 flex items-center gap-2 text-sm text-ink-soft transition hover:text-bordeaux"><ArrowLeft size={16} /> Retour au calendrier</button>
            <p className="font-sans text-lg font-medium capitalize text-ink">{longDayParis(daySlots[0])}</p>
            <p className="mb-4 text-xs text-ink-muted">Choisis ton horaire (heure de Paris)</p>
            <div className="grid grid-cols-3 gap-2 sm:grid-cols-4">
              {daySlots.map((iso) => (
                <button key={iso} data-testid={`slot-${iso}`} onClick={() => setSelectedSlot(iso)}
                  className="flex items-center justify-center gap-1.5 rounded-xl border border-bordeaux-border py-2.5 text-sm font-medium text-bordeaux transition hover:bg-bordeaux hover:text-white">
                  <Clock size={13} /> {timeParis(iso)}
                </button>
              ))}
            </div>
          </>
        ) : (
          <>
            <button onClick={() => setSelectedSlot(null)} className="mb-4 flex items-center gap-2 text-sm text-ink-soft transition hover:text-bordeaux"><ArrowLeft size={16} /> Changer d'horaire</button>
            <div className="mb-5 rounded-xl bg-bordeaux-tint/40 px-4 py-3 text-sm text-bordeaux">
              <span className="font-medium capitalize">{longDayParis(selectedSlot)}</span> à <span className="font-medium">{timeParis(selectedSlot)}</span> · 30 min
            </div>
            <div className="space-y-5">
              {questions.map((q, i) => (
                <div key={i}>
                  <label className="block text-sm font-medium text-ink">{q.name}{q.required && <span className="text-bordeaux"> *</span>}</label>
                  <textarea
                    data-testid={`booking-question-${i}`}
                    rows={3}
                    value={answers[i] || ""}
                    onChange={(e) => setAnswers((a) => a.map((v, j) => (j === i ? e.target.value : v)))}
                    className="mt-2 w-full rounded-xl border border-line bg-cream/40 p-3 text-sm outline-none transition focus:border-bordeaux focus:bg-white"
                    placeholder="Ta réponse…"
                  />
                </div>
              ))}
              <button
                data-testid="confirm-booking"
                onClick={submitBooking}
                disabled={booking}
                className="flex w-full items-center justify-center gap-2 rounded-full bg-bordeaux py-3.5 text-sm font-medium text-white transition hover:bg-bordeaux-dark disabled:opacity-60"
              >
                {booking ? <><Loader2 size={16} className="animate-spin" /> Réservation…</> : <>Confirmer mon rendez-vous</>}
              </button>
            </div>
          </>
        )}
      </div>
    </motion.div>
  );
}
